import { Link } from 'react-router'
import type { Rec } from '../api'
import { fmtDate, kindOf, timeAgo } from '../format'
import { Chart, type Marker } from './Chart'
import { Conv, DirPill, MetricText, PodAv } from './primitives'

function sparkMarkers(r: Rec): Marker[] {
  const closes = r.spark.closes
  if (!r.entry_ts || !closes || r.spark.entryIndex >= closes.length) return []
  return [{ index: r.spark.entryIndex, kind: kindOf(r.type) }]
}

// The card colour follows the call's result, not the raw price line: a SELL
// whose stock fell is green.
function tone(r: Rec): 'up' | 'down' | 'flat' {
  if (r.pick_return_pct == null) return 'flat'
  return r.pick_return_pct > 0 ? 'up' : r.pick_return_pct < 0 ? 'down' : 'flat'
}

function Since({ r }: { r: Rec }) {
  return r.entry_ts ? <>since {fmtDate(r.episode_pub_date)}</> : <>priced next session</>
}

// m_screens.jsx MHome card.
export function RecCardMobile({ r }: { r: Rec }) {
  return (
    <Link to={`/rec/${r.id}`} className="m-card m-rec" data-testid="rec-card">
      <div className="hd">
        <div style={{ minWidth: 0 }}>
          <div className="tk">
            <span className="tick" style={{ fontSize: 17 }}>{r.ticker}</span>
            <DirPill type={r.type} />
          </div>
          <div className="co">{r.company_name}</div>
        </div>
        <div className="mv">
          <span className="big"><MetricText pct={r.pick_return_pct} usd={r.pick_return_usd} /></span>
          <span className="s"><Since r={r} /></span>
        </div>
      </div>
      <Chart values={r.spark.closes ?? []} markers={sparkMarkers(r)} height={48} variant="mini" tone={tone(r)} />
      <div className="ft">
        <div className="src">
          <PodAv name={r.podcast_name} image={r.podcast_image_url} size="tiny" />
          <span>{r.podcast_name}</span>
        </div>
        <div className="row ai-c g-10" style={{ flexShrink: 0 }}>
          <Conv n={r.conviction} />
          <span>{timeAgo(r.episode_pub_date)} ago</span>
        </div>
      </div>
    </Link>
  )
}

// page_home.jsx card.
export function RecCardDesktop({ r }: { r: Rec }) {
  return (
    <Link to={`/rec/${r.id}`} className="rec-card" style={{ color: 'inherit', textDecoration: 'none' }} data-testid="rec-card">
      <div className="rec-head">
        <div className="tl">
          <div className="tick-row">
            <span className="tick" style={{ fontSize: 16 }}>{r.ticker}</span>
            <DirPill type={r.type} />
          </div>
          <span className="company">{r.company_name}</span>
        </div>
        <div className="metric-block">
          <span className="metric-main"><MetricText pct={r.pick_return_pct} usd={r.pick_return_usd} /></span>
          <span className="metric-sub">{r.entry_ts ? <>since rec · {fmtDate(r.episode_pub_date)}</> : 'priced next session'}</span>
        </div>
      </div>
      <div style={{ margin: '-2px -4px 0' }}>
        <Chart values={r.spark.closes ?? []} markers={sparkMarkers(r)} height={54} variant="mini" tone={tone(r)} />
      </div>
      <div className="rec-foot">
        <div className="src">
          <PodAv name={r.podcast_name} image={r.podcast_image_url} />
          <span className="name">{r.podcast_name}</span>
        </div>
        <div className="row ai-c g-10">
          <Conv n={r.conviction} />
          <span className="ago">{timeAgo(r.episode_pub_date)} ago</span>
        </div>
      </div>
    </Link>
  )
}
